// Advanced Corner Buffer System for L-shape and U-shape closets
// Buffers prevent collisions at wall intersections (corner joins)
// A buffer is a RESERVED / NON-PLACEABLE vertical zone near a corner edge

import { v4 as uuidv4 } from 'uuid';

export const BUFFER_DEFAULTS = {
  MIN_BUFFER_INCHES: 30, // Fallback when no depth exists
  DEPTH_MULTIPLIER: 1.5, // bufferIn = 1.5 * depthOfOtherWall
};

// ==================== DATA MODEL ====================
// buffersByWall = {
//   'wall-a': { start: Buffer|null, end: Buffer|null },
//   'wall-b': { start: Buffer|null, end: Buffer|null },
//   'wall-c': { start: Buffer|null, end: Buffer|null }
// }
//
// Each Buffer:
// {
//   id: string,
//   sourceCorner: 'AB' | 'AC',
//   sizeIn: number,
//   pinnedSide: 'start' | 'end',
//   targetWall: 'wall-a' | 'wall-b' | 'wall-c'
// }

/**
 * Calculate buffer size based on the OTHER intersecting wall's depth
 * bufferIn = 1.5 * depthOfOtherWall
 */
export const calculateBufferSize = (depthIn) => {
  if (!depthIn || depthIn <= 0) {
    return BUFFER_DEFAULTS.MIN_BUFFER_INCHES;
  }
  return BUFFER_DEFAULTS.DEPTH_MULTIPLIER * depthIn;
};

/**
 * Get the walls that intersect at a corner
 */
export const getCornerWalls = (cornerId) => {
  if (cornerId === 'AB') return ['wall-a', 'wall-b'];
  if (cornerId === 'AC') return ['wall-a', 'wall-c'];
  return [];
};

/**
 * Get all corners for a shape type
 */
export const getCornersForShape = (shapeType) => {
  if (shapeType === 'L_SHAPE') return ['AB'];
  if (shapeType === 'U_SHAPE') return ['AB', 'AC'];
  return [];
};

/**
 * Get walls that can host a buffer (only designed walls)
 * Uses the standardized 3-wall system
 */
export const getValidTargetWalls = (cornerId, shapeType, lShapeDoorSide = 'right') => {
  // Only designed walls can receive buffers
  if (shapeType === 'L_SHAPE') {
    // L-Shape: 2 walls are designed
    return lShapeDoorSide === 'left' ? ['wall-b', 'wall-c'] : ['wall-a', 'wall-b'];
  }
  if (shapeType === 'U_SHAPE') return ['wall-a', 'wall-b', 'wall-c'];
  return ['wall-a']; // 1-Wall
};

/**
 * Get all available walls for buffer placement (only designed walls)
 * Uses the standardized 3-wall system
 */
export const getAllValidWalls = (shapeType, lShapeDoorSide = 'right') => {
  if (shapeType === 'L_SHAPE') {
    return lShapeDoorSide === 'left' ? ['wall-b', 'wall-c'] : ['wall-a', 'wall-b'];
  }
  if (shapeType === 'U_SHAPE') return ['wall-a', 'wall-b', 'wall-c'];
  return ['wall-a']; // 1-Wall
};

/**
 * Get all designed walls from project
 * Helper to filter walls with designed: true
 */
export const getDesignedWalls = (walls) => {
  return walls.filter(w => w.designed !== false);
};

/**
 * Get human-readable wall name
 */
export const getWallDisplayName = (wallId) => {
  const names = {
    'wall-a': 'Wall A',
    'wall-b': 'Wall B',
    'wall-c': 'Wall C',
  };
  return names[wallId] || wallId;
};

/**
 * Get the default pinned side for a buffer on a wall
 * Based on which corner it comes from
 */
export const getDefaultPinnedSide = (cornerId, wallId) => {
  // L-shape: Corner AB
  // - On Wall A: buffer at RIGHT (end) where A meets B
  // - On Wall B: buffer at LEFT (start) where B meets A
  
  // U-shape: Corner AB and AC
  // - Wall A: AB buffer at LEFT (start), AC buffer at RIGHT (end)
  // - Wall B: AB buffer at LEFT (start)
  // - Wall C: AC buffer at LEFT (start)
  
  if (cornerId === 'AB') {
    if (wallId === 'wall-a') return 'end';   // A's right side meets B
    if (wallId === 'wall-b') return 'start'; // B's left side meets A
  }
  
  if (cornerId === 'AC') {
    if (wallId === 'wall-a') return 'start'; // A's left side meets C
    if (wallId === 'wall-c') return 'start'; // C's left side meets A
  }
  
  return 'start';
};

/**
 * Create a new buffer object
 */
export const createBuffer = (cornerId, targetWall, depthIn, pinnedSide = null) => {
  return {
    id: uuidv4(),
    sourceCorner: cornerId,
    sizeIn: calculateBufferSize(depthIn),
    pinnedSide: pinnedSide || getDefaultPinnedSide(cornerId, targetWall),
    targetWall,
  };
};

/**
 * Initialize default buffers for a shape type
 * L-shape: 1 buffer at the corner intersection
 *   - Door Left (Walls B+C): Buffer on Wall C at start (B↔C corner)
 *   - Door Right (Walls A+B): Buffer on Wall B at start (A↔B corner)
 * U-shape: 2 buffers - Wall B (start) and Wall C (start)
 */
export const initializeBuffers = (shapeType, depthIn = 24, lShapeDoorSide = 'right') => {
  const buffersByWall = {
    'wall-a': { start: null, end: null },
    'wall-b': { start: null, end: null },
    'wall-c': { start: null, end: null },
  };
  
  if (shapeType === 'L_SHAPE') {
    if (lShapeDoorSide === 'left') {
      // Door Left: Walls B + C are active
      // Buffer goes on Wall C at start (the B↔C intersection)
      // Corner is "BC" conceptually for Door Left configuration
      const bcBuffer = createBuffer('BC', 'wall-c', depthIn, 'start');
      buffersByWall['wall-c'].start = bcBuffer;
    } else {
      // Door Right: Walls A + B are active
      // Corner AB buffer defaults to Wall B, pinned at start
      const abBuffer = createBuffer('AB', 'wall-b', depthIn, 'start');
      buffersByWall['wall-b'].start = abBuffer;
    }
  }
  
  if (shapeType === 'U_SHAPE') {
    // Corner AB buffer defaults to Wall B, pinned at start
    const abBuffer = createBuffer('AB', 'wall-b', depthIn, 'start');
    buffersByWall['wall-b'].start = abBuffer;
    
    // Corner AC buffer defaults to Wall C, pinned at start
    const acBuffer = createBuffer('AC', 'wall-c', depthIn, 'start');
    buffersByWall['wall-c'].start = acBuffer;
  }
  
  return buffersByWall;
};

/**
 * Get all buffers on a specific wall
 */
export const getBuffersOnWall = (buffersByWall, wallId) => {
  const wallBuffers = buffersByWall?.[wallId];
  if (!wallBuffers) return [];
  
  const result = [];
  if (wallBuffers.start) result.push({ ...wallBuffers.start, position: 'start' });
  if (wallBuffers.end) result.push({ ...wallBuffers.end, position: 'end' });
  return result;
};

/**
 * Get buffer count on a wall
 */
export const getBufferCount = (buffersByWall, wallId) => {
  const wallBuffers = buffersByWall?.[wallId];
  if (!wallBuffers) return 0;
  return (wallBuffers.start ? 1 : 0) + (wallBuffers.end ? 1 : 0);
};

/**
 * Check if a wall has a buffer at a specific position
 */
export const hasBufferAt = (buffersByWall, wallId, position) => {
  return buffersByWall?.[wallId]?.[position] !== null;
};

/**
 * Find a buffer by its corner source
 */
export const findBufferByCorner = (buffersByWall, cornerId) => {
  for (const wallId of Object.keys(buffersByWall)) {
    const wallBuffers = buffersByWall[wallId];
    if (wallBuffers.start?.sourceCorner === cornerId) {
      return { buffer: wallBuffers.start, wallId, position: 'start' };
    }
    if (wallBuffers.end?.sourceCorner === cornerId) {
      return { buffer: wallBuffers.end, wallId, position: 'end' };
    }
  }
  return null;
};

/**
 * Calculate usable width for a wall (accounting for both start and end buffers)
 */
export const calculateUsableWidth = (buffersByWall, wallId, wallLength) => {
  const wallBuffers = buffersByWall?.[wallId];
  if (!wallBuffers) {
    return { 
      usableWidth: wallLength, 
      startOffset: 0, 
      endOffset: 0,
      startBuffer: null,
      endBuffer: null,
    };
  }
  
  const startOffset = wallBuffers.start?.sizeIn || 0;
  const endOffset = wallBuffers.end?.sizeIn || 0;
  const usableWidth = Math.max(0, wallLength - startOffset - endOffset);
  
  return {
    usableWidth,
    startOffset,
    endOffset,
    startBuffer: wallBuffers.start,
    endBuffer: wallBuffers.end,
  };
};

/**
 * Check if a column placement is valid (not in buffer zones)
 */
export const validateColumnPlacement = (columnX, columnWidth, wallLength, usableInfo) => {
  const { startOffset, endOffset, startBuffer, endBuffer } = usableInfo;
  const columnEnd = columnX + columnWidth;
  
  // Check start buffer
  if (startBuffer && columnX < startOffset) {
    return {
      valid: false,
      error: `Cannot place in buffer zone (first ${startOffset}" reserved for corner clearance)`,
    };
  }
  
  // Check end buffer
  if (endBuffer && columnEnd > wallLength - endOffset) {
    return {
      valid: false,
      error: `Cannot place in buffer zone (last ${endOffset}" reserved for corner clearance)`,
    };
  }
  
  return { valid: true, error: null };
};

/**
 * Validate if existing layout fits within usable width
 */
export const validateLayoutFits = (items, wallId, wallLength, usableInfo) => {
  const wallItems = items.filter(i => i.wallId === wallId);
  if (wallItems.length === 0) return { valid: true };
  
  const { startOffset, endOffset } = usableInfo;
  
  // Check if any item is in start buffer zone
  const minX = Math.min(...wallItems.map(i => i.x));
  if (minX < startOffset) {
    return {
      valid: false,
      error: 'Cannot move buffer: wall layout exceeds usable width. Reduce columns first.',
    };
  }
  
  // Check if any item extends into end buffer zone
  const maxExtent = Math.max(...wallItems.map(i => i.x + i.width));
  if (maxExtent > wallLength - endOffset) {
    return {
      valid: false,
      error: 'Cannot move buffer: wall layout exceeds usable width. Reduce columns first.',
    };
  }
  
  return { valid: true };
};

/**
 * Change buffer pinned side (left/right)
 * Returns new buffersByWall or error
 */
export const changePinnedSide = (buffersByWall, bufferId, newSide, items, walls) => {
  // Find the buffer
  let foundBuffer = null;
  let foundWallId = null;
  let foundPosition = null;
  
  for (const wallId of Object.keys(buffersByWall)) {
    const wallBuffers = buffersByWall[wallId];
    if (wallBuffers.start?.id === bufferId) {
      foundBuffer = wallBuffers.start;
      foundWallId = wallId;
      foundPosition = 'start';
      break;
    }
    if (wallBuffers.end?.id === bufferId) {
      foundBuffer = wallBuffers.end;
      foundWallId = wallId;
      foundPosition = 'end';
      break;
    }
  }
  
  if (!foundBuffer) {
    return { success: false, error: 'Buffer not found' };
  }
  
  // If already at desired side, no change needed
  if (foundPosition === newSide) {
    return { success: true, buffersByWall };
  }
  
  // Check if target side is occupied
  if (buffersByWall[foundWallId][newSide] !== null) {
    return { 
      success: false, 
      error: `Cannot pin to ${newSide}: that side already has a buffer.`,
    };
  }
  
  // Validate layout fits with new position
  const wall = walls.find(w => w.id === foundWallId);
  if (wall) {
    // Create temporary buffer config to check
    const tempBuffers = JSON.parse(JSON.stringify(buffersByWall));
    tempBuffers[foundWallId][foundPosition] = null;
    tempBuffers[foundWallId][newSide] = { ...foundBuffer, pinnedSide: newSide };
    
    const usableInfo = calculateUsableWidth(tempBuffers, foundWallId, wall.length);
    const validation = validateLayoutFits(items, foundWallId, wall.length, usableInfo);
    
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }
  }
  
  // Perform the move
  const newBuffersByWall = JSON.parse(JSON.stringify(buffersByWall));
  newBuffersByWall[foundWallId][foundPosition] = null;
  newBuffersByWall[foundWallId][newSide] = { ...foundBuffer, pinnedSide: newSide };
  
  return { success: true, buffersByWall: newBuffersByWall };
};

/**
 * Send buffer to another wall
 * Any wall (A, B, C) can receive buffers - max 2 per wall
 * Handles collision: auto-place on other side if one is occupied
 * Returns new buffersByWall or error
 */
export const sendBufferToWall = (buffersByWall, bufferId, targetWallId, items, walls, shapeType, lShapeDoorSide = 'right') => {
  // Find the buffer
  let foundBuffer = null;
  let sourceWallId = null;
  let sourcePosition = null;
  
  for (const wallId of Object.keys(buffersByWall)) {
    const wallBuffers = buffersByWall[wallId];
    if (wallBuffers.start?.id === bufferId) {
      foundBuffer = wallBuffers.start;
      sourceWallId = wallId;
      sourcePosition = 'start';
      break;
    }
    if (wallBuffers.end?.id === bufferId) {
      foundBuffer = wallBuffers.end;
      sourceWallId = wallId;
      sourcePosition = 'end';
      break;
    }
  }
  
  if (!foundBuffer) {
    return { success: false, error: 'Buffer not found' };
  }
  
  // Validate target wall exists for this shape (L-shape depends on door side)
  const validWalls = getAllValidWalls(shapeType, lShapeDoorSide);
  if (!validWalls.includes(targetWallId)) {
    return { 
      success: false, 
      error: `${getWallDisplayName(targetWallId)} is not available for this closet type`,
    };
  }
  
  // If already on target wall, no change needed
  if (sourceWallId === targetWallId) {
    return { success: true, buffersByWall };
  }
  
  // Check target wall's buffer slots
  const targetSlots = buffersByWall[targetWallId];
  const targetBufferCount = (targetSlots.start ? 1 : 0) + (targetSlots.end ? 1 : 0);
  
  if (targetBufferCount >= 2) {
    return {
      success: false,
      error: `${getWallDisplayName(targetWallId)} already has 2 buffers. Move one first.`,
    };
  }
  
  // Determine which side to place on target wall
  // Default to 'start' (left), unless it's occupied
  let targetSide = 'start';
  if (targetSlots.start !== null) {
    targetSide = 'end';
  }
  
  // Validate layout on target wall fits with new buffer
  const targetWall = walls.find(w => w.id === targetWallId);
  if (targetWall) {
    const tempBuffers = JSON.parse(JSON.stringify(buffersByWall));
    tempBuffers[sourceWallId][sourcePosition] = null;
    tempBuffers[targetWallId][targetSide] = { 
      ...foundBuffer, 
      pinnedSide: targetSide,
      targetWall: targetWallId,
    };
    
    const usableInfo = calculateUsableWidth(tempBuffers, targetWallId, targetWall.length);
    const validation = validateLayoutFits(items, targetWallId, targetWall.length, usableInfo);
    
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }
  }
  
  // Perform the move
  const newBuffersByWall = JSON.parse(JSON.stringify(buffersByWall));
  newBuffersByWall[sourceWallId][sourcePosition] = null;
  newBuffersByWall[targetWallId][targetSide] = {
    ...foundBuffer,
    pinnedSide: targetSide,
    targetWall: targetWallId,
  };
  
  return { 
    success: true, 
    buffersByWall: newBuffersByWall,
    placedAt: targetSide,
  };
};

/**
 * Update all buffer sizes when depth changes
 */
export const updateBufferSizes = (buffersByWall, newDepthIn) => {
  if (!buffersByWall) return buffersByWall;
  
  const newSize = calculateBufferSize(newDepthIn);
  const updated = JSON.parse(JSON.stringify(buffersByWall));
  
  for (const wallId of Object.keys(updated)) {
    if (updated[wallId].start) {
      updated[wallId].start.sizeIn = newSize;
    }
    if (updated[wallId].end) {
      updated[wallId].end.sizeIn = newSize;
    }
  }
  
  return updated;
};

/**
 * Delete a buffer from the system
 * Returns updated buffersByWall or error
 */
export const deleteBuffer = (buffersByWall, bufferId) => {
  if (!buffersByWall || !bufferId) {
    return { success: false, error: 'Invalid parameters' };
  }
  
  // Find and remove the buffer
  let found = false;
  const newBuffersByWall = JSON.parse(JSON.stringify(buffersByWall));
  
  for (const wallId of Object.keys(newBuffersByWall)) {
    const wallBuffers = newBuffersByWall[wallId];
    if (wallBuffers.start?.id === bufferId) {
      newBuffersByWall[wallId].start = null;
      found = true;
      break;
    }
    if (wallBuffers.end?.id === bufferId) {
      newBuffersByWall[wallId].end = null;
      found = true;
      break;
    }
  }
  
  if (!found) {
    return { success: false, error: 'Buffer not found' };
  }
  
  return { success: true, buffersByWall: newBuffersByWall };
};

/**
 * Resize a buffer
 * @param {object} buffersByWall - Current buffer state
 * @param {string} bufferId - Buffer ID to resize
 * @param {number} newSizeIn - New size in inches
 * @param {array} items - All closet items (for collision check)
 * @param {array} walls - All walls
 * @returns {object} { success, buffersByWall, error }
 */
export const resizeBuffer = (buffersByWall, bufferId, newSizeIn, items, walls) => {
  if (!buffersByWall || !bufferId) {
    return { success: false, error: 'Invalid parameters' };
  }
  
  // Validate size (minimum 6 inches)
  if (newSizeIn < 6) {
    return { success: false, error: 'Buffer size must be at least 6 inches' };
  }
  
  // Find the buffer
  let foundBuffer = null;
  let foundWallId = null;
  let foundPosition = null;
  
  for (const wallId of Object.keys(buffersByWall)) {
    const wallBuffers = buffersByWall[wallId];
    if (wallBuffers.start?.id === bufferId) {
      foundBuffer = wallBuffers.start;
      foundWallId = wallId;
      foundPosition = 'start';
      break;
    }
    if (wallBuffers.end?.id === bufferId) {
      foundBuffer = wallBuffers.end;
      foundWallId = wallId;
      foundPosition = 'end';
      break;
    }
  }
  
  if (!foundBuffer) {
    return { success: false, error: 'Buffer not found' };
  }
  
  // Get wall length for validation
  const wall = walls.find(w => w.id === foundWallId);
  if (!wall) {
    return { success: false, error: 'Wall not found' };
  }
  
  // Basic validation: buffer must be non-negative
  if (newSizeIn < 0) {
    return { success: false, error: 'Buffer size cannot be negative' };
  }
  
  // Calculate the other buffer size on this wall
  const otherBuffer = foundPosition === 'start' 
    ? buffersByWall[foundWallId].end 
    : buffersByWall[foundWallId].start;
  const otherBufferSize = otherBuffer?.sizeIn || 0;
  
  // Logical safety bound: buffer + other buffer must be less than wall width
  // There must be at least 1" of usable space
  const maxAllowedSize = wall.length - otherBufferSize - 1;
  
  if (newSizeIn >= wall.length - otherBufferSize) {
    return { 
      success: false, 
      error: `Buffer must be smaller than the wall width. Maximum allowed: ${Math.floor(maxAllowedSize)}"` 
    };
  }
  
  // Create temp buffer config to validate
  const tempBuffers = JSON.parse(JSON.stringify(buffersByWall));
  tempBuffers[foundWallId][foundPosition] = { ...foundBuffer, sizeIn: newSizeIn };
  
  // Check if items still fit
  const usableInfo = calculateUsableWidth(tempBuffers, foundWallId, wall.length);
  const validation = validateLayoutFits(items, foundWallId, wall.length, usableInfo);
  
  if (!validation.valid) {
    return { 
      success: false, 
      error: 'Cannot resize: existing components would be in buffer zone. Move or remove components first.' 
    };
  }
  
  // Apply the resize
  const newBuffersByWall = JSON.parse(JSON.stringify(buffersByWall));
  newBuffersByWall[foundWallId][foundPosition].sizeIn = newSizeIn;
  
  return { success: true, buffersByWall: newBuffersByWall };
};

/**
 * Get starting X position for new items (after start buffer)
 */
export const getPlacementStartX = (buffersByWall, wallId) => {
  const startBuffer = buffersByWall?.[wallId]?.start;
  return startBuffer ? startBuffer.sizeIn : 0;
};

/**
 * Get ending X position for new items (before end buffer)
 */
export const getPlacementEndX = (buffersByWall, wallId, wallLength) => {
  const endBuffer = buffersByWall?.[wallId]?.end;
  return endBuffer ? wallLength - endBuffer.sizeIn : wallLength;
};

/**
 * Migrate from old corner buffer format to new buffersByWall format
 */
export const migrateOldBufferFormat = (oldCornerBuffers, shapeType, depthIn) => {
  if (!oldCornerBuffers || Object.keys(oldCornerBuffers).length === 0) {
    return initializeBuffers(shapeType, depthIn);
  }
  
  // Check if already in new format
  if (oldCornerBuffers['wall-a'] !== undefined) {
    return oldCornerBuffers;
  }
  
  // Migrate from old format
  const buffersByWall = {
    'wall-a': { start: null, end: null },
    'wall-b': { start: null, end: null },
    'wall-c': { start: null, end: null },
  };
  
  // Old format: { AB: { ownerWall, bufferIn, cornerSide }, AC: ... }
  for (const cornerId of Object.keys(oldCornerBuffers)) {
    const oldBuffer = oldCornerBuffers[cornerId];
    const targetWall = oldBuffer.ownerWall;
    const pinnedSide = getDefaultPinnedSide(cornerId, targetWall);
    
    const newBuffer = {
      id: uuidv4(),
      sourceCorner: cornerId,
      sizeIn: oldBuffer.bufferIn || calculateBufferSize(depthIn),
      pinnedSide,
      targetWall,
    };
    
    buffersByWall[targetWall][pinnedSide] = newBuffer;
  }
  
  return buffersByWall;
};
